import { defer } from '@/components/utils/defer';
import { PopupMonitor } from '..';
import { PopupAdapter } from '../types';
import { KlaviyoOnsiteStorage } from '@/@types/klaviyo';

declare global {
  interface Window {
    popupMonitor: PopupMonitor;
  }
}
export class KlaviyoPopupAdapter implements PopupAdapter {
  private id: string;
  private shouldShow: () => Promise<boolean>;
  private afterShown: () => Promise<void>;
  private afterHidden: () => Promise<void>;
  private forceShow: boolean;

  constructor({
    id,
    shouldShow,
    afterHidden,
    afterShown,
    forceShow,
  }: {
    id: string;
    shouldShow?: () => Promise<boolean>;
    afterShown?: () => Promise<void>;
    afterHidden?: () => Promise<void>;
    forceShow?: boolean;
  }) {
    this.id = id;
    this.shouldShow = shouldShow ?? (() => Promise.resolve(true));
    this.afterShown = afterShown ?? (() => Promise.resolve());
    this.afterHidden = afterHidden ?? (() => Promise.resolve());

    this.forceShow = typeof forceShow === 'undefined' ? false : forceShow;

    defer(() => {
      const rootElement = this.getRootElement();
      if (!rootElement) return;

      // Set up mutation observer to check if the element has disappeared from the DOM
      const observer = new MutationObserver(() => {
        if (!this.getRootElement()) {
          console.debug(`Klaviyo form ${this.getId()} removed from DOM`);
          window.popupMonitor.hidePopup(this.id);

          observer.disconnect();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }, `[data-testid="klaviyo-form-${this.id}"]`);
  }

  getRootElement(): HTMLDivElement | null {
    const startingElement = document.querySelector(`[data-testid="klaviyo-form-${this.id}"]`);

    if (!startingElement) {
      return null;
    }

    // Scan all the way back up the tree until the parentElement is body
    let rootElement: HTMLDivElement = startingElement as HTMLDivElement;
    while (rootElement.parentElement && rootElement.parentElement.tagName !== 'BODY') {
      rootElement = rootElement.parentElement as HTMLDivElement;
    }

    return rootElement;
  }

  async show(): Promise<boolean> {
    // Don't show if the form has been disabled by the user (closed manually)
    // Only run this check if forceShow is false
    if (!this.forceShow) {
      let klaviyoOnsite = localStorage.getItem('klaviyoOnsite');
      if (klaviyoOnsite) {
        let parsedKlaviyoOnsite: KlaviyoOnsiteStorage = JSON.parse(klaviyoOnsite);

        if (parsedKlaviyoOnsite.viewedForms.modal.disabledForms[this.id]) {
          return false;
        }
      }
    }

    const shouldShow = await this.shouldShow();
    if (!shouldShow) return false;

    const element = this.getRootElement();

    if (!element) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', this.id]);

      setTimeout(() => {
        const element = this.getRootElement();

        if (!element) {
          console.error('Klaviyo form element not found after 1 second');
          return;
        }
        element.style.removeProperty('display');
      }, 1000);
    } else {
      element.style.removeProperty('display');
    }

    await this.afterShown();

    return true;
  }

  async hide(): Promise<boolean> {
    const element = this.getRootElement();

    // Cleanup already happened elsewhere
    if (element) {
      element.style.setProperty('display', 'none', 'important');
    }

    await this.afterHidden();

    return true;
  }

  isVisible(): boolean {
    const element = this.getRootElement();
    return !!element;
  }

  getId(): string {
    return this.id;
  }

  getForceShow(): boolean {
    return this.forceShow;
  }
}

//@ts-ignore
window.KlaviyoPopupAdapter = KlaviyoPopupAdapter;
